import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import styles from './ProjectTile.module.css';
import GlobalEmptyState from './GlobalEmptyState';
import { FilterBar } from './FilterBar';
import { Favicon } from '../utils/favicon';

interface Project {
  id: string;
  projectName: string;
  projectDescription?: string;
  companyId?: string;
}

interface ProjectTileProps {
  project: Project;
  companyWebsite?: string;
  onClick?: () => void;
}

export function ProjectTile({ project, companyWebsite, onClick }: ProjectTileProps) {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const navigate = useNavigate();
  const { t } = useLocale();
  const [documentCount, setDocumentCount] = useState<number>(0);
  const [loadingDocumentCount, setLoadingDocumentCount] = useState<boolean>(true);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      onClick();
    } else {
      // Navigate to project detail page
      navigate(`/project/${project.id}`);
    }
  };

  // Fetch document count for this project
  useEffect(() => {
    const fetchDocumentCount = async () => {
      if (!project.id) {
        setDocumentCount(0);
        setLoadingDocumentCount(false);
        return;
      }

      try {
        setLoadingDocumentCount(true);
        const documentsRef = collection(db, 'projects', project.id, 'documents');
        const querySnapshot = await getDocs(documentsRef);
        setDocumentCount(querySnapshot.size);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching document count for project:', error);
        }
        setDocumentCount(0);
      } finally {
        setLoadingDocumentCount(false);
      }
    };

    fetchDocumentCount();
  }, [project.id]);

  return (
    <div className={styles.projectTile} onClick={handleClick}>

      {/* Project icon */}
      <div className={styles.projectIcon}>
        {companyWebsite ? (
          <Favicon
            websiteUrl={companyWebsite}
            size={24}
            retinaMultiplier={4}
          />
        ) : (
          <img
            src={isDarkMode ? "/assets/icons/chart.bar.svg" : "/assets/icons/chart.bar.black.svg"}
            alt={t('project_tile_project_alt')}
            width={24}
            height={24}
          />
        )}
      </div>

      {/* Project information */}
      <div className={styles.projectInfo}>

        {/* Project name */}
        <h3 className={styles.projectTitle}>
          {project.projectName || t('project_tile_unknown_name')}
        </h3>

        {/* Project description */}
        <p className={styles.projectDescription}>
          {project.projectDescription || t('project_tile_unknown_description')}
        </p>

      </div>

      {/* Status badge */}
      <div className={styles.statusContainer}>
        <div className={`${styles.statusBadge} ${documentCount > 0 ? styles.statusDocuments : styles.statusEmpty}`}>
          {loadingDocumentCount ? t('project_tile_loading_documents') || '...' : `${documentCount} ${t('project_tile_documents_count') || 'documents'}`}
        </div>
      </div>
    </div>
  );
}

interface Company {
  id: string;
  website?: string;
}

interface ProjectListProps {
  projects: Project[];
  companies?: Company[];
  loading: boolean;
  searchValue: string;
  onSearchChange: (value: string) => void;
  onAddProject?: () => void;
}

export function ProjectList({
  projects,
  companies = [],
  loading,
  searchValue,
  onSearchChange,
  onAddProject
}: ProjectListProps) {
  const { t } = useLocale();

  // Create a lookup map for companies by ID
  const companyMap = React.useMemo(() => {
    const map = new Map<string, Company>();
    companies.forEach(company => {
      map.set(company.id, company);
    });
    return map;
  }, [companies]);

  // Filter projects by search query
  const filteredProjects = projects.filter(project => {
    if (!searchValue) return true;
    const searchLower = searchValue.toLowerCase();
    return (
      project.projectName?.toLowerCase().includes(searchLower) ||
      project.projectDescription?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className={styles.projectList}>
      <FilterBar
        filters={[]}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={t('project_tile_search_placeholder') || 'Search projects by name...'}
        showInviteButton={true}
        onAddUser={onAddProject}
        addButtonText={t('project_tile_add_project') || 'Add Project'}
      />

      {loading ? (
        <div className={styles.loadingContainer}>
          <p>{t('project_tile_loading_projects') || 'Loading projects...'}</p>
        </div>
      ) : filteredProjects.length === 0 ? (
        <GlobalEmptyState />
      ) : (
        <div>
          {filteredProjects.map((project) => {
            const company = project.companyId ? companyMap.get(project.companyId) : undefined;
            return (
              <div key={project.id} className={styles.projectItem}>
                <ProjectTile
                  project={project}
                  companyWebsite={company?.website}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

